import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import {
  floorLayoutV2Endpoint,
  saveFloorLayoutV2Endpoint,
  uploadFloorBackgroundV2Endpoint,
  uploadUnitSvgV2Endpoint,
} from '../../withdrawal/services/projectService';
import { getUnitsForModelEndpoint } from '../services/investorService';
import { FloorLayoutV2Detail, UnitDistributionV2 } from '../models/towerLayoutModelV2';

export interface EditableUnitV2 {
  id: string; // unique ID in front (e.g. UUID)
  unit_id: string; // the model unit ID mapped to this SVG placement
  unit_name?: string;
  model_name?: string;
  x: number;
  y: number;
  svgWidth: number;
  svgHeight: number;
  svgViewBox: string;
  path: string;
  fileName: string;
  svg_url: string;
  placed: boolean;
  isNew?: boolean;
  status?: string;
}

export interface PaletteSvgV2 {
  id: string;
  fileName: string;
  viewBox: string;
  width: number;
  height: number;
  pathD: string;
  svgUrl: string;
}

const useProjectFloorLayoutV2 = () => {
  const { id = '', id_dist = '' } = useParams();
  const { callEndpoint, isLoading } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const [floorInfo, setFloorInfo] = useState<FloorLayoutV2Detail | null>(null);
  const [placedUnits, setPlacedUnits] = useState<EditableUnitV2[]>([]);
  const [svgPalette, setSvgPalette] = useState<PaletteSvgV2[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [availableUnits, setAvailableUnits] = useState<any[]>([]);

  const loadFloorLayout = useCallback(async () => {
    // 1. Fetch floor layout details
    const { status: floorStatus, data: floorResponse } = await callEndpoint(
      floorLayoutV2Endpoint(id, id_dist, token),
    );
    if (floorStatus === 200) {
      const details: FloorLayoutV2Detail = floorResponse?.data;
      setFloorInfo(details);
      if (details?.distribution) {
        const mappedUnits: EditableUnitV2[] = details.distribution.map((d) => ({
          id: d.id || crypto.randomUUID(),
          unit_id: d.unit_id,
          unit_name: d.unit_name,
          model_name: d.model_name,
          x: d.svg_layout?.x || 0,
          y: d.svg_layout?.y || 0,
          svgWidth: d.svg_layout?.svgWidth || 100,
          svgHeight: d.svg_layout?.svgHeight || 100,
          svgViewBox: d.svg_layout?.svgViewBox || '0 0 100 100',
          path: d.svg_layout?.path || '',
          fileName: d.svg_layout?.fileName || '',
          svg_url: d.svg_url,
          status: d.status,
          placed: !!(d.svg_url && d.svg_layout),
          isNew: false,
        }));
        setPlacedUnits(mappedUnits);
      }
    }

    // 2. Fetch available units in the project
    const { status: unitsStatus, data: unitsResponse } = await callEndpoint(
      getUnitsForModelEndpoint(id, token),
    );
    if (unitsStatus === 200) {
      setAvailableUnits(unitsResponse?.data || []);
    }
  }, [id, id_dist, token]);

  const handleUploadSvgFile = async (file: File) => {
    // 1. Parse file in browser
    const text = await file.text();
    const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    const path = doc.querySelector('path');

    const viewBox = svg?.getAttribute('viewBox') || '0 0 100 100';
    const width = parseFloat(svg?.getAttribute('width') || '100');
    const height = parseFloat(svg?.getAttribute('height') || '100');
    const pathD = path?.getAttribute('d') || '';

    // 2. Upload file to GCS via backend
    const formData = new FormData();
    formData.append('file', file);

    const { status, data: responseData } = await callEndpoint(
      uploadUnitSvgV2Endpoint(id, id_dist, formData, token),
    );

    if (status === 200 || status === 201) {
      const gcsUrl = responseData?.data?.raw_svg_url || responseData?.data?.svg_url;
      const newPaletteSvg: PaletteSvgV2 = {
        id: crypto.randomUUID(),
        fileName: file.name,
        viewBox,
        width,
        height,
        pathD,
        svgUrl: gcsUrl,
      };
      setSvgPalette((prev) => [...prev, newPaletteSvg]);
      return newPaletteSvg;
    } else {
      alert('Error al subir el archivo SVG');
      return null;
    }
  };

  const handleUploadBackground = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append('cover', file);

    const { status, data: responseData } = await callEndpoint(
      uploadFloorBackgroundV2Endpoint(id, id_dist, formData, token),
    );

    if (status === 200 || status === 201) {
      const newBg = responseData?.data?.facade_background;
      setFloorInfo((prev) => prev ? { ...prev, level_facade_background: newBg } : null);
      return newBg;
    } else {
      alert('Error al subir el fondo del piso');
      return null;
    }
  };

  const handleSave = async (unitsToSave: EditableUnitV2[]): Promise<boolean> => {
    const payloadUnits = unitsToSave
      .filter((u) => u.svg_url && u.path)
      .map((u) => ({
        id: u.isNew ? undefined : u.id,
        unit_id: u.unit_id || null,
        svg_url: u.svg_url,
        svg_layout: {
          x: u.x,
          y: u.y,
          svgWidth: u.svgWidth,
          svgHeight: u.svgHeight,
          svgViewBox: u.svgViewBox,
          path: u.path,
          fileName: u.fileName,
        },
      }));

    const payload = {
      units: payloadUnits,
    };

    const { status } = await callEndpoint(
      saveFloorLayoutV2Endpoint(id, id_dist, payload, token),
    );

    if (status === 200 || status === 201) {
      loadFloorLayout();
      return true;
    } else {
      return false;
    }
  };

  return {
    floorInfo,
    placedUnits,
    setPlacedUnits,
    svgPalette,
    setSvgPalette,
    selectedUnitId,
    setSelectedUnitId,
    availableUnits,
    loadFloorLayout,
    handleUploadSvgFile,
    handleUploadBackground,
    handleSave,
    isLoading,
  };
};

export default useProjectFloorLayoutV2;
